import styled from "@emotion/styled"
import Typography from "@mui/material/Typography"
import { richTextListStyles } from "../../common/richTextListStyles"
import { breakpoints } from "../../theme/mediaQueries"
import { sizes } from "../../theme/sizes"
import { colors } from "../../theme/colors"

export const Root = styled.div`
  width: 100%;
  padding: ${sizes.spacing[48]}px 0;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
  /* EGDE-76: when the anchor nav scrolls to this block, leave room for the
     sticky header so the section heading isn't hidden under it. Matches the
     RichContentAnchorNav scroll-spy OFFSET. */
  scroll-margin-top: 120px;

  @media (min-width: ${breakpoints.lg}px) {
    padding: ${sizes.spacing[64]}px 0;
    gap: ${sizes.spacing[32]}px;
  }
`

export const SectionTitle = styled(Typography)`
  color: ${colors.light.text.primary};
`

export const RichTextWrapper = styled.div`
  width: 100%;
  max-width: 100%;
  /* EGDE-118 keeps wide pasted content from producing page-level horizontal
     scroll. EGDE-609: clipping exactly at the content edge sliced any italic
     glyph sitting against it. The brand face ships no italic, so the browser
     synthesises an oblique whose ink shears past the glyph's own advance box
     (measured 1.0–2.3px at 20px). In Hebrew that lands on the right-hand edge,
     where every line begins. The clip margin gives that ink room to paint while
     keeping the containment.

     Both axes must be clip for the margin to take effect — with
     overflow-x clip plus overflow-y visible, Chrome ignores
     overflow-clip-margin entirely and cuts at the edge, exactly like the
     overflow-x hidden this replaced. Clipping both axes also avoids that
     declaration's side effect of forcing overflow-y to auto. */
  overflow: clip;
  overflow-clip-margin: 6px;
  display: flex;
  flex-direction: column;
  gap: ${sizes.spacing[24]}px;
  color: ${colors.primitives.neutral600};

  & h1 {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
  }

  & h2 {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
  }

  & h3 {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
    margin: 0;
  }

  & h4 {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.4;
    margin: 0;
  }

  & p {
    font-size: 18px;
    line-height: 22px;
    margin: 0;
  }

  & strong,
  & b {
    font-weight: 700;
  }

  & em,
  & i {
    font-style: italic;
  }

  & img {
    width: 100%;
    height: auto;
    border-radius: ${sizes.borderRadius[16]}px;
    display: block;
  }

  /* EGDE-336: embedded iframes (YouTube etc.) render responsively (16/9)
     instead of honouring intrinsic width/height attributes. Design decision
     2026-07-08: mobile = end-to-end full bleed with square corners; desktop =
     grid width with 40px rounded corners. */
  & iframe {
    width: 100vw;
    max-width: 100vw;
    margin-inline: calc(50% - 50vw);
    aspect-ratio: 16 / 9;
    height: auto;
    border: 0;
    border-radius: 0;
    display: block;

    @media (min-width: ${breakpoints.lg}px) {
      width: 100%;
      max-width: 100%;
      margin-inline: 0;
      border-radius: ${sizes.borderRadius[40]}px;
    }
  }

  & a {
    /* EGDE-327: rich-text body hyperlinks render blue, not orange/green */
    /* !important overrides inline color:#... pasted into CMS rich text */
    color: ${colors.primitives.linkBlue} !important;
    text-decoration: underline;
  }

  /* EGDE-330 (round 2): QA rejected the plain orange discs — the design marks
     list items with the small brand chevron. Shared with every other
     rich-text renderer via richTextListStyles. */
  ${richTextListStyles}

  & li {
    font-size: 18px;
    line-height: 22px;
  }

  & blockquote {
    border-inline-start: 3px solid ${colors.primitives.primary500};
    padding-inline-start: ${sizes.spacing[16]}px;
    margin: 0;
    font-style: italic;
  }

  & table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    & th,
    & td {
      padding: ${sizes.spacing[8]}px ${sizes.spacing[12]}px;
      border: 1px solid ${colors.light.border.default};
      font-size: 16px;
      line-height: 1.5;
      text-align: start;
    }

    & th {
      font-weight: 700;
      background-color: ${colors.light.bg.secondary};
    }
  }

  @media (min-width: ${breakpoints.lg}px) {
    & h1 {
      font-size: 40px;
    }

    & h2 {
      font-size: 32px;
    }

    & h3 {
      font-size: 28px;
    }

    & p,
    & li {
      font-size: 20px;
      line-height: 28px;
    }
  }
`
